import React, { useMemo, useCallback } from 'react';
import type { ColumnDef, Row } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Check, X } from 'lucide-react';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { EmployeeImage } from '@/components/EmployeeImage';
import { AnomalyIndicator } from '@/components/AnomalyIndicator';
import { PolicyViolationAlert } from '@/components/timesheet/PolicyViolationAlert';
import { OvertimeTypeBadge } from '@/components/timesheet/OvertimeTypeBadge';
import { ApprovalEmptyState } from '@/components/ApprovalEmptyState';
import type { Timesheet } from '@/types/timesheet';
import type { AnomalyDetectionResult } from '@/types/anomaly';
import type { PolicyViolation } from '@/data/mockPolicyViolations';

// ─── Stage badge helpers ──────────────────────────────────────────────────

const STAGE_COLOURS = [
  'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300',
  'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300',
  'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300',
  'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300',
  'bg-pink-100 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300',
  'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300',
];

function stageColour(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return STAGE_COLOURS[hash % STAGE_COLOURS.length];
}

function StageBadge({ name, order, total }: { name: string; order?: number; total?: number }) {
  const colour = stageColour(name);
  const progress = order != null && total != null ? ` (${order}/${total})` : '';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colour} whitespace-nowrap`}
    >
      {name}{progress}
    </span>
  );
}

function WaitingBadge({ submittedAt }: { submittedAt?: Date | null }) {
  if (!submittedAt) return <span className="text-xs font-medium text-muted-foreground">—</span>;

  const totalDays = Math.floor((Date.now() - submittedAt.getTime()) / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;

  let label: string;
  if (totalDays === 0) label = '< 1d';
  else if (weeks === 0) label = `${days}d`;
  else if (days === 0) label = `${weeks}w`;
  else label = `${weeks}w ${days}d`;

  const colour =
    totalDays > 7
      ? 'text-red-600 dark:text-red-400'
      : totalDays >= 3
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-green-600 dark:text-green-500';

  return <span className={`text-xs font-normal tabular-nums ${colour}`}>{label}</span>;
}

// ─── Row data shape ───────────────────────────────────────────────────────────
export interface ApprovalRow {
  timesheet: Timesheet;
  employeeName: string;
  department: string;
  weekEnding: Date;
  totalHours: number;
  policyViolations: PolicyViolation[];
  anomalyResult: AnomalyDetectionResult | undefined;
  isOverdue: boolean;
  photoUrl: string | undefined;
  /** Manager this row is being approved on behalf of (undefined = own queue) */
  delegationManagerId?: string;
  delegationManagerName?: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────
interface ApprovalGridProps {
  rows: ApprovalRow[];
  /** Called by DataTableEnhanced whenever the TanStack selection state changes */
  onSelectionChange: (selectedRows: ApprovalRow[]) => void;
  /** When flipped true→false DataTableEnhanced clears its internal rowSelection */
  clearSelection: boolean;
  onView: (ts: Timesheet) => void;
  onApprove: (ts: Timesheet) => void;
  onReject: (ts: Timesheet) => void;
  canApproveReject: () => boolean;
  /** Whether the current user can bulk-approve (and bulk-reject) timesheets */
  canBulkApprove: () => boolean;
  /** Whether the current user can see policy violation data per timesheet row */
  canViewPolicyViolations: () => boolean;
  /** Whether the current user can see detected anomaly/issue data per timesheet row */
  canViewDetectedIssues: () => boolean;
  currentEmployeeId: number;
  pendingCount: number;
  /** Quick-filter controls rendered on the right of the search bar */
  customFilters: React.ReactNode;
  /** Bulk-approve button rendered on the right side of the toolbar (next to FieldSelector) */
  toolbarActions: React.ReactNode;
  /** Active filter chips rendered as a row below the toolbar */
  filterChips: React.ReactNode;
  /** Whether overtime tracking is enabled for this organisation */
  overtimeEnabled: boolean;
  /** Active overtime types for this organisation — used to resolve badge colours */
  overtimeTypes: Array<{ id: number; name: string; badgeColour: string }>;
}

export function ApprovalGrid({
  rows,
  onSelectionChange,
  clearSelection,
  onView,
  onApprove,
  onReject,
  canApproveReject,
  canBulkApprove,
  canViewPolicyViolations,
  canViewDetectedIssues,
  currentEmployeeId,
  pendingCount,
  customFilters,
  toolbarActions,
  filterChips,
  overtimeEnabled,
  overtimeTypes,
}: ApprovalGridProps) {
  const isRowSelectable = useCallback(
    (row: Row<ApprovalRow>) => {
      const ts = row.original.timesheet;
      const isSelfApproval =
        ts.cannotApproveOwnTimesheet === true && ts.employeeId === currentEmployeeId;
      return !isSelfApproval;
    },
    [currentEmployeeId]
  );

  const bulkEnabled = canBulkApprove();

  const columns: ColumnDef<ApprovalRow>[] = useMemo(
    () => [
      // ── Row Selection (only when Bulk Approve & Reject permission is on) ─
      ...(bulkEnabled ? [{
        id: 'select',
        header: ({ table }: { table: import('@tanstack/react-table').Table<ApprovalRow> }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value: boolean | 'indeterminate') => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
            className="translate-y-[2px]"
            data-tour="approvals-bulk-select"
          />
        ),
        cell: ({ row }: { row: Row<ApprovalRow> }) => {
          const ts = row.original.timesheet;
          const isSelfApproval =
            ts.cannotApproveOwnTimesheet === true && ts.employeeId === currentEmployeeId;
          return (
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(value: boolean | 'indeterminate') => row.toggleSelected(!!value)}
              aria-label="Select row"
              className="translate-y-[2px]"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              disabled={isSelfApproval}
              title={isSelfApproval ? 'Cannot select own timesheet for approval' : undefined}
            />
          );
        },
        enableSorting: false,
        enableHiding: false,
        size: 40,
      } satisfies ColumnDef<ApprovalRow>] : []),

      // ── Employee ────────────────────────────────────────────────────────
      {
        accessorKey: 'employeeName',
        header: 'Employee',
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <EmployeeImage
              name={row.original.employeeName}
              photoUrl={row.original.photoUrl}
              size="md"
            />
            <div className="flex flex-col gap-0.5 min-w-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView(row.original.timesheet);
                }}
                className="text-xs font-medium text-left hover:underline hover:text-primary cursor-pointer transition-colors"
              >
                {row.original.employeeName}
              </button>
            </div>
          </div>
        ),
        enableSorting: true,
        size: 220,
      },

      // ── Department ──────────────────────────────────────────────────────
      {
        accessorKey: 'department',
        header: 'Department',
        cell: ({ row }) => (
          <span className="text-xs font-normal">{row.original.department || '—'}</span>
        ),
        enableSorting: true,
        size: 140,
      },

      // ── Delegated From ──────────────────────────────────────────────────
      {
        accessorKey: 'delegationManagerName',
        header: 'Delegated From',
        meta: { label: 'Delegated From' },
        cell: ({ row }) => (
          row.original.delegationManagerName ? (
            <span className="text-xs font-normal text-foreground">
              {row.original.delegationManagerName}
            </span>
          ) : (
            <span className="text-xs font-normal text-muted-foreground">—</span>
          )
        ),
        enableSorting: true,
        size: 160,
      },

      // ── Week Ending ─────────────────────────────────────────────────────
      {
        accessorKey: 'weekEnding',
        header: 'Week Ending',
        cell: ({ row }) => (
          <span className="text-xs font-normal">
            {format(row.original.weekEnding, 'dd MMM yyyy')}
          </span>
        ),
        enableSorting: true,
        size: 130,
      },

      // ── Total Hours ─────────────────────────────────────────────────────
      {
        accessorKey: 'totalHours',
        header: 'Hours',
        cell: ({ row }) => (
          <span className="text-xs font-medium tabular-nums">
            {row.original.totalHours.toFixed(1)}h
          </span>
        ),
        enableSorting: true,
        size: 90,
      },

      // ── Overtime Types ──────────────────────────────────────────────────
      ...(overtimeEnabled
        ? [
            {
              id: 'overtime',
              header: 'Overtime',
              accessorFn: (row: ApprovalRow) => {
                const types = row.timesheet.entries
                  .filter((e) => e.overtimeTypeId != null && e.overtimeTypeName)
                  .map((e) => e.overtimeTypeId as number);
                return [...new Set(types)].length;
              },
              cell: ({ row }: { row: { original: ApprovalRow } }) => {
                // Collect unique overtime types present in this timesheet's entries.
                // Resolve badge colour from the live overtimeTypes list; fall back to
                // a neutral grey if the type has been deleted since the entry was saved.
                const seen = new Map<number, { name: string; colour: string }>();
                for (const entry of row.original.timesheet.entries) {
                  if (entry.overtimeTypeId != null && entry.overtimeTypeName) {
                    if (!seen.has(entry.overtimeTypeId)) {
                      const liveType = overtimeTypes.find((t) => t.id === entry.overtimeTypeId);
                      seen.set(entry.overtimeTypeId, {
                        name: entry.overtimeTypeName,
                        colour: liveType?.badgeColour ?? '#6b7280',
                      });
                    }
                  }
                }
                if (seen.size === 0) {
                  return <span className="text-xs font-medium text-muted-foreground">—</span>;
                }
                return (
                  <div className="flex flex-wrap gap-1">
                    {[...seen.entries()].map(([id, { name, colour }]) => (
                      <OvertimeTypeBadge key={id} name={name} colour={colour} />
                    ))}
                  </div>
                );
              },
              enableSorting: true,
              size: 160,
            } as ColumnDef<ApprovalRow>,
          ]
        : []),

      // ── Policy Violations ───────────────────────────────────────────────
      // Only rendered when the role has viewPolicyViolations permission enabled.
      ...(canViewPolicyViolations()
        ? [
            {
              id: 'violations',
              header: 'Violations',
              accessorFn: (row: ApprovalRow) => row.policyViolations.length,
              cell: ({ row }: { row: { original: ApprovalRow } }) =>
                row.original.policyViolations.length > 0 ? (
                  <PolicyViolationAlert violations={row.original.policyViolations} compact />
                ) : (
                  <span className="text-xs font-medium text-muted-foreground">None</span>
                ),
              enableSorting: true,
              size: 130,
            } as ColumnDef<ApprovalRow>,
          ]
        : []),

      // ── Stage ────────────────────────────────────────────────────────────
      {
        id: 'stage',
        header: 'Stage',
        accessorFn: (row) => row.timesheet.currentStageName ?? '',
        cell: ({ row }) =>
          row.original.timesheet.currentStageName ? (
            <StageBadge
              name={row.original.timesheet.currentStageName}
              order={row.original.timesheet.currentStageOrder}
              total={row.original.timesheet.totalStages}
            />
          ) : (
            <span className="text-xs font-medium text-muted-foreground">—</span>
          ),
        enableSorting: true,
        size: 150,
      },

      // ── Waiting ──────────────────────────────────────────────────────────
      {
        id: 'waiting',
        header: 'Waiting',
        accessorFn: (row) =>
          row.timesheet.submittedAt
            ? Date.now() - row.timesheet.submittedAt.getTime()
            : 0,
        cell: ({ row }) => (
          <WaitingBadge submittedAt={row.original.timesheet.submittedAt} />
        ),
        enableSorting: true,
        size: 90,
      },

      // ── Status / Anomaly ─────────────────────────────────────────────────
      // Only rendered when the role has viewDetectedIssues permission enabled.
      // The Overdue badge is always shown regardless (it is not an anomaly detection result).
      ...(canViewDetectedIssues()
        ? [
            {
              id: 'status',
              header: 'Status',
              accessorFn: (row: ApprovalRow) =>
                (row.isOverdue ? 1 : 0) + (row.anomalyResult?.hasAnomalies ? 1 : 0),
              cell: ({ row }: { row: { original: ApprovalRow } }) => (
                <div className="flex items-center gap-1 flex-wrap">
                  {row.original.anomalyResult && (
                    <AnomalyIndicator anomalyResult={row.original.anomalyResult} size="sm" />
                  )}
                  {row.original.isOverdue && (
                    <Badge variant="destructive" className="text-xs">
                      Overdue
                    </Badge>
                  )}
                  {!row.original.anomalyResult?.hasAnomalies && !row.original.isOverdue && (
                    <span className="text-xs font-medium text-muted-foreground">—</span>
                  )}
                </div>
              ),
              enableSorting: true,
              size: 140,
            } as ColumnDef<ApprovalRow>,
          ]
        : []),

      // ── Actions ──────────────────────────────────────────────────────────
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const ts = row.original.timesheet;
          const isSelfApproval =
            ts.cannotApproveOwnTimesheet === true && ts.employeeId === currentEmployeeId;
          const disabled = !canApproveReject() || isSelfApproval;
          const approveTitle = !canApproveReject()
            ? "You don't have permission to approve timesheets"
            : isSelfApproval
              ? 'Cannot approve own timesheet'
              : 'Approve timesheet';
          const rejectTitle = !canApproveReject()
            ? "You don't have permission to reject timesheets"
            : isSelfApproval
              ? 'Cannot reject own timesheet'
              : 'Reject timesheet';

          return (
            <div
              className="flex items-center justify-end gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => onApprove(ts)}
                        disabled={disabled}
                      >
                        <Check className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">Approve</span>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{approveTitle}</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onReject(ts)}
                        disabled={disabled}
                      >
                        <X className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">Reject</span>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{rejectTitle}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false,
        size: 130,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bulkEnabled, canApproveReject, currentEmployeeId, onView, onApprove, onReject, overtimeEnabled, overtimeTypes]
  );

  const fieldLabels: Record<string, string> = {
    employeeName: 'Employee',
    department: 'Department',
    weekEnding: 'Week Ending',
    totalHours: 'Hours',
    overtime: 'Overtime',
    violations: 'Violations',
    stage: 'Stage',
    waiting: 'Waiting',
    status: 'Status',
    delegationManagerName: 'Delegated From',
  };

  const fieldCategories: Record<string, string> = {
    employeeName: 'People',
    department: 'People',
    weekEnding: 'Time',
    totalHours: 'Time',
    overtime: 'Time',
    violations: 'Issues',
    stage: 'Workflow',
    waiting: 'Workflow',
    status: 'Issues',
  };

  if (pendingCount === 0) {
    return <ApprovalEmptyState />;
  }

  return (
    <DataTableEnhanced
      columns={columns}
      data={rows}
      onRowClick={(row) => onView((row as ApprovalRow).timesheet)}
      searchKey="employeeName"
      searchPlaceholder="Search by employee name..."
      enableGrouping={false}
      enableColumnVisibility={true}
      enableSorting={true}
      enableFiltering={true}
      enableRowSelection={bulkEnabled ? isRowSelectable : false}
      onSelectionChange={onSelectionChange}
      clearSelection={clearSelection}
      pageSize={20}
      storageKey="approvals-grid"
      fieldLabels={fieldLabels}
      fieldCategories={fieldCategories}
      showSortIconsWhenNotSorted={true}
      hideFieldSelector={false}
      customFilters={customFilters}
      toolbarActions={toolbarActions}
      filterChips={filterChips}
    />
  );
}
